import { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { AutoComplete, Input, Radio, message } from 'antd';
import { IpcButton, IpcFormField, IpcModal, IpcStatusBadge } from '../components/ui';
import { nodeAutoCompleteRuleApi } from '../api/services';
import type { NodeAutoCompleteRuleTriggerResultVO } from '../api/types/nodeAutoCompleteRule';
import type { AutoCompleteTriggerType, NodeAutoCompleteRule } from '../types/nodeAutoCompleteRule';
import { DEFAULT_TEST_TRIGGER_PAYLOAD_JSON } from '../types/nodeAutoCompleteRuleScriptContext';
import { useApolloMqConfig } from '../hooks/useApolloMqConfig';
import './NodeAutoCompleteRuleTriggerModal.css';

const { TextArea } = Input;

type Props = {
  open: boolean;
  onClose: () => void;
  /** 当前列表规则，用于补全 triggerKey / MQ 建议 */
  rules: NodeAutoCompleteRule[];
};

function parseJsonInput(raw: string, label: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) return {};
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    throw new Error(`${label} 不是合法的 JSON`);
  }
}

function uniqOptions(values: string[]): { value: string }[] {
  const seen = new Set<string>();
  const out: { value: string }[] = [];
  for (const raw of values) {
    const v = (raw ?? '').trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push({ value: v });
  }
  return out;
}

function summarizeTriggerResult(result: NodeAutoCompleteRuleTriggerResultVO): string {
  const matched = result.matchedRuleCount ?? result.matched_rule_count ?? 0;
  const ok = result.successRuleCount ?? result.success_rule_count ?? 0;
  const fail = result.failedRuleCount ?? result.failed_rule_count ?? 0;
  const err = result.errorMessage ?? result.error_message;
  if (matched === 0) return err || '未匹配到启用规则';
  if (fail > 0) return `匹配 ${matched} 条，成功 ${ok}，失败 ${fail}${err ? `：${err}` : ''}`;
  return `匹配 ${matched} 条，全部成功`;
}

const NodeAutoCompleteRuleTriggerModal: FC<Props> = ({ open, onClose, rules }) => {
  const { vhostOptions, queueOptions } = useApolloMqConfig();
  const [triggerType, setTriggerType] = useState<AutoCompleteTriggerType>('api');
  const [triggerKey, setTriggerKey] = useState('');
  const [mqVhost, setMqVhost] = useState('');
  const [mqQueue, setMqQueue] = useState('');
  const [payloadJson, setPayloadJson] = useState(DEFAULT_TEST_TRIGGER_PAYLOAD_JSON);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<NodeAutoCompleteRuleTriggerResultVO | null>(null);

  useEffect(() => {
    if (!open) return;
    setTriggerType('api');
    setTriggerKey('');
    setMqVhost('');
    setMqQueue('');
    setPayloadJson(DEFAULT_TEST_TRIGGER_PAYLOAD_JSON);
    setLastResult(null);
  }, [open]);

  const triggerKeyOptions = useMemo(
    () =>
      uniqOptions(
        rules
          .filter(r => r.triggerType === 'api' && r.triggerKey)
          .flatMap(r => (r.triggerKey ?? '').split(/[,，]/)),
      ),
    [rules],
  );

  const mqVhostSuggest = useMemo(() => {
    const fromRules = rules
      .filter(r => r.triggerType === 'mq' && r.mqVhost)
      .map(r => r.mqVhost as string);
    const fromApollo = vhostOptions.map(o => o.value);
    return uniqOptions([...fromApollo, ...fromRules]);
  }, [rules, vhostOptions]);

  const mqQueueSuggest = useMemo(() => {
    const fromRules = rules
      .filter(r => r.triggerType === 'mq' && (!mqVhost || r.mqVhost === mqVhost) && r.mqQueue)
      .flatMap(r => (r.mqQueue ?? '').split(/[,，]/));
    const fromApollo = queueOptions.map(o => o.value);
    return uniqOptions([...fromApollo, ...fromRules]);
  }, [rules, mqVhost, queueOptions]);

  const handleSubmit = async () => {
    if (triggerType === 'api') {
      if (!triggerKey.trim()) {
        message.warning('请填写触发 Key');
        return;
      }
    } else {
      if (!mqVhost.trim()) {
        message.warning('请填写 VHost');
        return;
      }
      if (!mqQueue.trim()) {
        message.warning('请填写队列名称');
        return;
      }
    }

    let triggerPayload: unknown;
    try {
      triggerPayload = parseJsonInput(payloadJson, 'triggerPayload');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'JSON 解析失败');
      return;
    }

    setSubmitting(true);
    setLastResult(null);
    try {
      const result =
        triggerType === 'api'
          ? await nodeAutoCompleteRuleApi.triggerByApi(triggerKey.trim(), triggerPayload)
          : await nodeAutoCompleteRuleApi.triggerByMq(mqVhost.trim(), mqQueue.trim(), triggerPayload);
      setLastResult(result);
      const matched = result.matchedRuleCount ?? result.matched_rule_count ?? 0;
      const fail = result.failedRuleCount ?? result.failed_rule_count ?? 0;
      const summary = summarizeTriggerResult(result);
      if (matched === 0) message.info(summary);
      else if (fail > 0 || result.success === false) message.warning(summary);
      else message.success(summary);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '触发失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <IpcModal
      open={open}
      onCancel={onClose}
      title="手动触发规则"
      width={560}
      destroyOnClose
      footer={(
        <div className="acr-trigger-modal__footer">
          <IpcButton onClick={onClose}>关闭</IpcButton>
          <IpcButton variant="primary" loading={submitting} onClick={() => void handleSubmit()}>
            执行触发
          </IpcButton>
        </div>
      )}
    >
      <div className="acr-trigger-modal__body">
        <IpcFormField label="触发方式" required>
          <Radio.Group
            value={triggerType}
            onChange={e => setTriggerType(e.target.value as AutoCompleteTriggerType)}
            options={[
              { value: 'api', label: '接口触发' },
              { value: 'mq', label: 'MQ 触发' },
            ]}
          />
        </IpcFormField>

        {triggerType === 'api' ? (
          <IpcFormField label="触发 Key" required>
            <AutoComplete
              value={triggerKey}
              options={triggerKeyOptions}
              onChange={setTriggerKey}
              placeholder="与规则绑定的 triggerKey，命中启用的接口规则"
              style={{ width: '100%' }}
              allowClear
            />
          </IpcFormField>
        ) : (
          <>
            <IpcFormField label="VHost" required>
              <AutoComplete
                value={mqVhost}
                options={mqVhostSuggest}
                onChange={v => {
                  setMqVhost(v);
                  setMqQueue('');
                }}
                placeholder="MQ Virtual Host"
                style={{ width: '100%' }}
                allowClear
              />
            </IpcFormField>
            <IpcFormField label="队列名称" required>
              <AutoComplete
                value={mqQueue}
                options={mqQueueSuggest}
                onChange={setMqQueue}
                placeholder="与规则绑定的队列（规则侧支持逗号分隔）"
                style={{ width: '100%' }}
                allowClear
              />
            </IpcFormField>
          </>
        )}

        <IpcFormField
          label={triggerType === 'api' ? 'content（请求 Body）' : 'triggerPayload（消息体）'}
        >
          <TextArea
            value={payloadJson}
            onChange={e => setPayloadJson(e.target.value)}
            rows={8}
            spellCheck={false}
            className="acr-trigger-modal__payload"
            placeholder={
              triggerType === 'api'
                ? '接口触发时此处作为 content；脚本侧看到 { trigger_key, content }'
                : 'MQ 触发时此处作为 content；脚本侧看到 { vhost, queue, content }'
            }
          />
        </IpcFormField>

        {lastResult ? (
          <div className="acr-trigger-modal__result">
            <div className="acr-trigger-modal__result-head">
              <IpcStatusBadge
                variant={
                  (lastResult.matchedRuleCount ?? lastResult.matched_rule_count ?? 0) === 0
                    ? 'muted'
                    : (lastResult.failedRuleCount ?? lastResult.failed_rule_count ?? 0) > 0
                      || lastResult.success === false
                      ? 'destructive'
                      : 'success'
                }
              >
                {summarizeTriggerResult(lastResult)}
              </IpcStatusBadge>
            </div>
            {(lastResult.rules ?? []).length > 0 ? (
              <ul className="acr-trigger-modal__rule-list">
                {(lastResult.rules ?? []).map(item => {
                  const id = item.ruleId ?? item.rule_id;
                  const name = item.ruleName ?? item.rule_name ?? `#${id}`;
                  const ok = item.success !== false;
                  const err = item.errorMessage ?? item.error_message;
                  const runId = item.runId ?? item.run_id;
                  return (
                    <li key={String(id)} className={ok ? 'ok' : 'fail'}>
                      <span className="name">{name}</span>
                      <span className="meta">
                        {ok ? `成功${runId != null ? ` · run#${runId}` : ''}` : `失败${err ? `：${err}` : ''}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </IpcModal>
  );
};

export default NodeAutoCompleteRuleTriggerModal;
